client import { EditorView, keymap } from "@codemirror/view";
client import { EditorState } from "@codemirror/state";
client import extensions, { update } from "app/util/codemirror";
client import { formatCode } from "app/util/format";
import { faPaintbrush } from "@fortawesome/free-solid-svg-icons";
import type { File } from "app/util/workspace";
import styles from "./editor.styles.module.scss";
export interface Input {
  files: File[];
  filesChange?: (files: File[]) => void;
  tab: number;
  tabChange?: (tab: number) => void;
}

static const ext = (path: string) => path.slice(path.lastIndexOf(".") + 1);

let/files:=input.files
let/selected:=input.tab
let/file=files[selected]
  ,valueChange(newValue) {
    files = files.toSpliced(selected, 1, newValue);
  }

// Bridges the format button (markup) to the editor's lifecycle; set on mount.
let/doFormat=() => {}

tabs files:=files selected:=selected
div class=styles.editorWrap
  div.shiki/$editor id=styles.editor
  button
    ,type="button"
    ,title="Format (Shift+Alt+F)"
    ,class=styles.action
    ,onClick() {
      doFormat();
    }
    fa-icon=faPaintbrush
    html-script --
      ${
        `if(navigator.platform.startsWith("Mac"))document.currentScript.parentElement.title="Format (⇧⌘F)"`
      };

lifecycle<{
  editor?: EditorView;
  listener?: ReturnType<typeof EditorView.updateListener.of>;
  currentPath?: string;
  change?: boolean;
  set(content: string): void;
  format(): void;
  stateFor(f: File | undefined): EditorState;
}>
  ,set(content) {
    this.change = content !== file.content;
    if (this.change) {
      file = { ...file, content };
    }
  }
  ,format() {
    const view = this.editor;
    if (!view || !file) return;
    const content = view.state.doc.toString();
    const cursorOffset = view.state.selection.main.head;
    const pending = formatCode(content, cursorOffset, ext(file.path));
    if (!pending) return;
    void pending
      .then((result) => {
        // Skip if the doc changed while formatting, or is already formatted.
        if (
          result.formatted === content ||
          view.state.doc.toString() !== content
        ) {
          return;
        }
        view.dispatch({
          changes: { from: 0, to: content.length, insert: result.formatted },
          selection: {
            anchor: Math.min(result.cursorOffset, result.formatted.length),
          },
        });
      })
      .catch(() => {
        // Ignore formatting errors (e.g. the file currently has a syntax error).
      });
  }
  ,stateFor(f) {
    return EditorState.create({
      doc: f?.content,
      extensions: [
        this.listener!,
        keymap.of([
          {
            // Alt+letter is a dead key on macOS, so use Cmd there instead.
            key: "Shift-Alt-f",
            mac: "Shift-Cmd-f",
            run: () => {
              this.format();
              return true;
            },
          },
        ]),
        ...extensions,
      ],
    });
  }
  ,onMount() {
    this.change = false;
    this.listener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        this.set(update.state.doc.toString());
      }
    });
    this.currentPath = file?.path;
    this.editor = new EditorView({
      state: this.stateFor(file),
      parent: $editor(),
    });
    doFormat = () => this.format();
    if (file) {
      update(this.editor, file.content, ext(file.path));
    }
  }
  ,onUpdate() {
    if (this.change) {
      this.change = false;
    } else if (file) {
      if (file.path !== this.currentPath) {
        // Switching tabs: give the new file its own editor state so undo
        // history and selection don't bleed across files.
        this.currentPath = file.path;
        this.editor!.setState(this.stateFor(file));
      }
      update(this.editor!, file.content, ext(file.path));
    }
  }
  ,onDestroy() {
    this.editor!.destroy();
  }
